import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/** The viewport where the rail turns into a drawer. It must match the breakpoint in `sidebar.css`. */
export const SIDEBAR_DRAWER_QUERY = '(max-width: 900px)';

/** The drawer state a portal shell hands to `PortalSidebar` and `SidebarToggle`. */
export interface SidebarDrawer {
  /** Whether the drawer is open — never true on a viewport wide enough for the rail. */
  open: boolean;
  show: () => void;
  hide: () => void;
}

/**
 * Drives the mobile drawer: open on request, only where the viewport is narrow enough for it, and closed again
 * whenever the route changes, so following a link never leaves the drawer covering the page it opened.
 */
export function useSidebarDrawer(): SidebarDrawer {
  const isDrawer = useMediaQuery(SIDEBAR_DRAWER_QUERY);
  const [requested, setRequested] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setRequested(false);
  }, [pathname]);

  const show = useCallback(() => setRequested(true), []);
  const hide = useCallback(() => setRequested(false), []);

  return { open: isDrawer && requested, show, hide };
}

/** Whether the media query matches, kept current as the viewport changes. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
